import React, { useEffect, useState } from "react";
import { useParams, useNavigate,useLocation } from "react-router-dom";
import { getExamenById, patchExamenById, getEvaPorId } from "../services/api";
import fieldConfig, { EXAMENES_SOLO_CONCLUSION } from "../config/fieldConfig";
import { sanitizePayload } from "../utils/sanitizePayload";
import {calcularIMC} from '../utils/imc';
import { confirmar } from './ConfirmModal';
import { toast } from 'react-toastify';

const EditarExamen = () => {
  const { evaluacionId, id_examen } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [parametrosSol,setParametrosSol] = useState(location.state?.parametros_sol || []);
  const [tipo, setTipo] = useState(null); // se llenará con id_tipo_examen
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const camposTablaVisual = [
    "ojo_der_lejos", "ojo_izq_lejos",
    "ojo_der_cerca", "ojo_izq_cerca"
  ];
  const baseFields = fieldConfig[tipo] || [];
  const fields = tipo === 'sangre' && parametrosSol.length > 0
    ? baseFields.filter(f => parametrosSol.includes(f.name))
    : baseFields;

  // Cargar examen existente
  useEffect(() => {
    const fetchExamenYParametros = async () => {
      try {
        const res = await getExamenById(evaluacionId, id_examen);
        if (res && res.data) {
          const examen = res.data;
          const elTipo = examen.id_tipo_examen;
          let params = location.state?.parametros_sol || [];
          if (elTipo === 'sangre' && params.length === 0){
            try{
              const evaRes = await getEvaPorId(evaluacionId);
              const relacionSangre = evaRes.data.tipos_examen.find(te => te.codigo === 'sangre');
              if (relacionSangre?.parametros_sol){
                params = relacionSangre.parametros_sol;
              }
            }catch(evaErr){
              console.error("Error al recuperar parámetros de la evaluacion para edición:",evaErr);
              toast.error("No se pudieron recuperar los parámetros de sangre de la evaluación.");
            }
          }
          setTipo(elTipo);
          setParametrosSol(params);
          // filtracion para mostrar los campos del examen de sangre pedido
          const camposConf = fieldConfig[elTipo] || [];
          const camposFiltrados = elTipo === 'sangre' && params.length > 0
            ? camposConf.filter(f => params.includes(f.name))
            : camposConf;

          setFormData({
            fecha_toma: examen.fecha_toma,
            fecha_disponibilidad: examen.fecha_disponibilidad,
            ...Object.fromEntries(
              camposFiltrados.map(f => [f.name, examen[f.name]])
            ),
            conclusion_examen: examen.conclusion_examen
              ? {
                estado_conclusion: examen.conclusion_examen.estado_conclusion || "",
                conclusion_txt: examen.conclusion_examen.conclusion_txt || ""
              }
              : { estado_conclusion: "", conclusion_txt: "" }
          });
        }
      } catch (err) {
        console.error("Error al cargar examen", err);
      } finally {
        setLoading(false);
      }
    };
    fetchExamenYParametros();
  }, [evaluacionId, id_examen,location.state?.parametros_sol]);

  useEffect(() => {
    if (tipo === 'antropometrico') {
      const peso = formData.peso;
      const talla = formData.talla;
      const nuevoIMC = calcularIMC(peso, talla);

      // Si el IMC en el estado es diferente al calculado (o si cambió el peso/talla), actualizamos
      if (formData.imc !== nuevoIMC) {
        setFormData(prev => ({
          ...prev,
          imc: nuevoIMC
        }));
      }
    }
  }, [formData.peso, formData.talla, tipo, formData.imc]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    let parsedValue = value;

    if (type === "number") {
      parsedValue = value === "" ? null : Number(value);
    }else if (type === "select-one"|| type==="text"){
      parsedValue = value.trim() ==="" ? null:value;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : parsedValue,
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    const confirmed = await confirmar("¿Seguro que quieres guardar los cambios en este examen?");
    if (!confirmed) return;

    const payload = sanitizePayload(formData, tipo);
    if (formData.conclusion_examen) {
      const { estado_conclusion, conclusion_txt } = formData.conclusion_examen;
      if (estado_conclusion || conclusion_txt) {
        payload.conclusion_examen = formData.conclusion_examen;
      }
    }
    if (EXAMENES_SOLO_CONCLUSION.includes(tipo)) {
      payload.comentarios = formData.conclusion_examen?.conclusion_txt || null;
    }
    try {
      await patchExamenById(evaluacionId, id_examen, payload);
      toast.success("Examen actualizado exitosamente");
      navigate(`/evaluaciones/${evaluacionId}/examenes`);
    } catch (err) {
      console.error("Error al actualizar examen", err);
      toast.error(err.response?.data?.message || "No se pudo actualizar el examen");
    }
  };

  if (loading) return <p>Cargando examen...</p>;
  if (!tipo) return <p>No se pudo determinar el tipo de examen</p>;

  return (
    <div className="content-wrapper">
      <h2>Editar Examen ({tipo})</h2>
      <form onSubmit={handleSubmit} className="container">
        <div className="form-group">
          <label>Fecha de Toma</label>
          <input
            type="date"
            name="fecha_toma"
            value={formData.fecha_toma || ""}
            onChange={handleChange}
            className="form-control"
          />
        </div>

        <div className="form-group">
          <label>Fecha Disponibilidad</label>
          <input
            type="date"
            name="fecha_disponibilidad"
            value={formData.fecha_disponibilidad || ""}
            onChange={handleChange}
            className="form-control"
          />
        </div>

        {tipo === "audiometria" ? (
          <div className="form-group" >
            <label>Audiometría</label>
            <table className="audiometria-table" >
              <thead>
                <tr>
                  <th></th>
                  <th>500 Hz</th>
                  <th>1000 Hz</th>
                  <th>2000 Hz</th>
                  <th>3000 Hz</th>
                  <th>4000 Hz</th>
                  <th>6000 Hz</th>
                  <th>8000 Hz</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>OD</td>
                  {["od500", "od1000", "od2000", "od3000", "od4000", "od6000", "od8000"].map(name => (
                    <td key={name}>
                      <input
                        type="number"
                        name={name}
                        value={formData[name] ?? ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </td>
                  ))}
                </tr>
                <tr>
                  <td>OI</td>
                  {["oi500", "oi1000", "oi2000", "oi3000", "oi4000", "oi6000", "oi8000"].map(name => (
                    <td key={name}>
                      <input
                        type="number"
                        name={name}
                        value={formData[name] ?? ""}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        ) :tipo === "vista"?(
          <div className="form-group">
            <label>Agudeza Visual (Medidas 20/XX)</label>
            <table className="visual-table">
              <thead>
                <tr>
                  <th>Visión</th>
                  <th>Ojo Derecho (OD)</th>
                  <th>Ojo Izquierdo (OI)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Lejos</td>
                  <td className="input-cell">
                    <div className="input-wrapper">
                      <span>20/</span>
                      <input
                        type="number"
                        name="ojo_der_lejos"
                        value={formData.ojo_der_lejos ?? ""}
                        onChange={handleChange}
                        className="form-control small-input"
                      />
                    </div>
                  </td>
                  <td className="input-cell">
                    <div className="input-wrapper">
                      <span>20/</span>
                      <input
                        type="number"
                        name="ojo_izq_lejos"
                        value={formData.ojo_izq_lejos ?? ""}
                        onChange={handleChange}
                        className="form-control small-input"
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>Cerca</td>
                  <td className="input-cell">
                    <div className="input-wrapper">
                      <span>20/</span>
                      <input
                        type="number"
                        name="ojo_der_cerca"
                        value={formData.ojo_der_cerca ?? ""}
                        onChange={handleChange}
                        className="form-control small-input"
                      />
                    </div>
                  </td>
                  <td className="input-cell">
                    <div className="input-wrapper">
                      <span>20/</span>
                      <input
                        type="number"
                        name="ojo_izq_cerca"
                        value={formData.ojo_izq_cerca ?? ""}
                        onChange={handleChange}
                        className="form-control small-input"
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            {fields
              .filter(f => !camposTablaVisual.includes(f.name))
              .map((field) => (
                <div key={field.name} className="form-group">
                  <label>{field.label}</label>
                  {field.type === "select" ? (
                    <select
                      name={field.name}
                      value={formData[field.name] ?? ""}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="">Seleccione...</option>
                      {field.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "checkbox" ? (
                    <input
                      type="checkbox"
                      name={field.name}
                      checked={!!formData[field.name]}
                      onChange={handleChange}
                    />
                  ) : (
                    <input
                      type={field.type}
                      name={field.name}
                      step={field.type === "number" ? "any" : undefined}
                      value={formData[field.name] ?? ""}
                      onChange={handleChange}
                      className="form-control"
                      readOnly={field.name === 'imc'}
                    />
                  )}
                </div>
              ))}
          </div>
        ) : (
          fields.map((field) => (
            <div key={field.name} className="form-group">
              <label>{field.label}</label>
              {field.type === "select" ? (
                <select
                  name={field.name}
                  value={formData[field.name] ?? ""}
                  onChange={handleChange}
                  className="form-control"
                >
                  <option value="">Seleccione...</option>
                  {field.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : field.type === "checkbox" ? (
                <input
                  type="checkbox"
                  name={field.name}
                  checked={!!formData[field.name]}
                  onChange={handleChange}
                />
              ) : (
                <input
                  type={field.type}
                  name={field.name}
                  step={field.type === "number" ? "any" : undefined}
                  value={formData[field.name] ?? ""}
                  onChange={handleChange}
                  className="form-control"
                  readOnly={field.name ==='imc'}
                />
              )}
            </div>
          ))
        )}
        <div className="form-group">
          <label>Estado de Conclusión</label>
          <select
            name="estado_conclusion"
            value={formData.conclusion_examen?.estado_conclusion || ""}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                conclusion_examen: {
                  ...prev.conclusion_examen,
                  estado_conclusion: e.target.value,
                },
              }))
            }
            className="form-control"
          >
            <option value="">Seleccione...</option>
            <option value="normal">Normal</option>
            <option value="alterado">Alterado</option>
            <option value="no aplica">No Aplica</option>
          </select>
        </div>

        <div className="form-group">
          <label>Texto de Conclusión</label>
          <textarea
            name="conclusion_txt"
            value={formData.conclusion_examen?.conclusion_txt || ""}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                conclusion_examen: {
                  ...prev.conclusion_examen,
                  conclusion_txt: e.target.value,
                },
              }))
            }
            className="form-control"
          />
        </div>

        <button type="submit" className="btn-submit">
          Guardar Cambios
        </button>
      </form>
    </div>
  );
};

export default EditarExamen;